import { useForumType } from '@/components/hooks/useForumType';
import React, { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useMutation } from '@apollo/client/react';
import { NetworkStatus } from '@apollo/client';
import { useQuery } from "@/lib/crud/useQuery";
import { gql } from '@/lib/generated/gql-codegen';
import { useCurrentUserId } from '../common/withUser';
import { useTracking } from "../../lib/analyticsEvents";
import { getContentTypes } from '../posts/PostsPage/ContentType';
import FooterTag, { tagStyle, smallTagTextStyle } from './FooterTag';
import classNames from 'classnames';
import { Card } from "@/components/widgets/Paper";
import { Link } from '../../lib/reactRouterWrapper';
import { forumSelect } from '../../lib/forumTypeUtils';
import { useMessages } from '../common/withMessages';
import { adminAccountSetting } from '../../lib/instanceSettings';
import stringify from 'json-stringify-deterministic';
import { AnnualReviewMarketInfo } from '../../lib/collections/posts/annualReviewMarkets';
import { stableSortTags } from '../../lib/collections/tags/helpers';
import HoverOver from "../common/HoverOver";
import ContentStyles from "../common/ContentStyles";
import Loading from "../vulcan-core/Loading";
import AddTagButton from "./AddTagButton";
import CoreTagsChecklist from "./CoreTagsChecklist";
import PostsAnnualReviewMarketTag from "../posts/PostsAnnualReviewMarketTag";
import ForumIcon from '../common/ForumIcon';
import { defineStyles } from '../hooks/defineStyles';
import { useStyles } from '../hooks/useStyles';
import { useCurrentTime } from '@/lib/utils/timeUtil';

const styles = defineStyles('FooterTagList', (theme: ThemeType) => ({
  root: {
    display: 'flex',
    gap: 4,
    flexWrap: 'wrap',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  alignRight: {
    justifyContent: 'flex-end'
  },
  allowTruncate: {
    display: "inline-flex",
    // Truncate to 1 row (webkit-line-clamp would be ideal here but it adds an ellipsis
    // which can't be removed)
    maxHeight: 33,
    overflow: "hidden",
  },
  overrideMargins: {
    marginTop: 0,
    marginBottom: 0,
  },
  postTypeLink: {
    "&:hover": {},
  },
  frontpageOrPersonal: {
    ...tagStyle(theme),
    backgroundColor: theme.palette.tag.hollowTagBackground,
    paddingTop: 4.5,
    paddingBottom: 4.5,
    border: theme.palette.tag.hollowTagBorder,
    color: theme.palette.text.dim3,
  },
  neverCoreStyling: {
    color: theme.palette.tag.text,
  },
  noBackground: {
    '&&&': {
      backgroundColor: 'transparent',
      border: 'none',
    }
  },
  card: {
    padding: 16,
    width: 450,
    paddingTop: 8,
  },
  smallText: {
    ...smallTagTextStyle(theme),
  },
  showAll: {
    fontFamily: theme.palette.fonts.sansSerifStack,
    fontSize: 14,
    fontWeight: 600,
    color: theme.palette.grey[500],
    cursor: "pointer",
    width: 'fit-content',
  },
  altAddTagButton: {
    backgroundColor: theme.palette.panelBackground.default,
    display: "inline-block",
    paddingLeft: 8,
    paddingTop: 4.5,
    paddingBottom: 4.5,
    paddingRight: 8,
    borderRadius: 3,
    fontWeight: 700,
    gap: 4,
    cursor: "pointer",
    border: theme.palette.tag.border
  },
}));

type FooterTagListPost = Pick<PostsList, '_id'|'tags'|'curatedDate'|'frontpageDate'|'reviewedByUserId'|'isEvent'|'postCategory'> & {
  postedAt?: string
}

const autoClassifiedIconStyles = defineStyles('AutoClassifiedIcon', (theme: ThemeType) => ({
  robotIcon: {
    marginLeft: 4,
    height: 12,
    opacity: 0.7,
    position: 'relative',
    top: 1,
  },
}));

const AutoClassifiedIcon = () => {
  const classes = useStyles(autoClassifiedIconStyles);
  return (
    <HoverOver title="This post was classified by our experimental auto-classifier (not an LLM). It will be reviewed by an admin within ~24 hours.">
      <ForumIcon icon="Robot" className={classes.robotIcon} />
    </HoverOver>
  );
}

const FooterTagList = ({
  post,
  hideScore,
  hideAddTag,
  //used for PostsPageSplashHeader
  useAltAddTagButton,
  smallText=false,
  showCoreTags,
  hidePostTypeTag,
  link=true,
  highlightAutoApplied=false,
  allowTruncate=false,
  overrideMargins=false,
  appendElement,
  annualReviewMarketInfo,
  align = "left",
  noBackground = false,
  neverCoreStyling = false,
  tagRight = true,
}: {
  post: FooterTagListPost,
  hideScore?: boolean,
  hideAddTag?: boolean,
  useAltAddTagButton?: boolean,
  showCoreTags?: boolean
  hidePostTypeTag?: boolean,
  smallText?: boolean,
  link?: boolean,
  highlightAutoApplied?: boolean,
  allowTruncate?: boolean,
  overrideMargins?: boolean,
  appendElement?: ReactNode,
  annualReviewMarketInfo?: AnnualReviewMarketInfo,
  align?: "left" | "right",
  noBackground?: boolean,
  neverCoreStyling?: boolean,
  tagRight?: boolean,
}) => {
  const { forumType } = useForumType();
  const classes = useStyles(styles);
  const [isAwaiting, setIsAwaiting] = useState(false);
  const rootRef = useRef<HTMLSpanElement>(null);
  const [showAll, setShowAll] = useState(!allowTruncate);
  const [displayShowAllButton, setDisplayShowAllButton] = useState(false);

  const currentUserId = useCurrentUserId();
  const { captureEvent } = useTracking()
  const { flash } = useMessages();

  // We already have the tags as a resolver on the post, this additional query
  // serves two purposes:
  // - It fetches more info that is only required on hover (the tagRel score,
  // the truncated description). Fetching this in a second round trip allows the
  // initial render to be faster.
  // - (somewhat speculative) It allows the mutation to be handled more seamlessly
  // (incrementing the score and reordering the tags) by updating the result of
  // this query
  // The fragment in this query must match the mutation below
  const { data, loading, refetch, networkStatus } = useQuery(gql(`
    query multiTagRelFooterTagListQuery($selector: TagRelSelector, $limit: Int, $enableTotal: Boolean) {
      tagRels(selector: $selector, limit: $limit, enableTotal: $enableTotal) {
        results {
          ...TagRelMinimumFragment
        }
        totalCount
      }
    }
  `), {
    variables: {
      selector: { tagsOnPost: { postId: post._id } },
      limit: 100,
      enableTotal: false,
    },
    fetchPolicy: 'cache-and-network',
    // Only fetch this as a follow-up query on the client
    ssr: false,
    notifyOnNetworkStatusChange: true,
  });

  const results = data?.tagRels?.results;
  const loadingInitial = networkStatus === NetworkStatus.loading;

  const checkShouldDisplayShowAll = useCallback(() => {
    if (!showAll && rootRef.current) {
      const el = rootRef.current;
      // Even when expanded scrollHeight and clientHeight can be off by a few pixels,
      // we are interested in the case where a whole row is hidden
      if (el.scrollHeight > (el.clientHeight + 10)) {
        setDisplayShowAllButton(true);
      } else {
        setDisplayShowAllButton(false);
      }
    }
  }, [showAll, rootRef, setDisplayShowAllButton]);

  // Check whether we should display the "Show all topics" button when:
  // - Results or loading state change
  // - The component window is resized
  const resultsSignature = stringify(results);
  useEffect(checkShouldDisplayShowAll, [showAll, loadingInitial, resultsSignature, checkShouldDisplayShowAll]);
  useEffect(() => {
    window.addEventListener("resize", checkShouldDisplayShowAll);
    return () => window.removeEventListener("resize", checkShouldDisplayShowAll);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onClickShowAll = useCallback(() => {
    setShowAll(true);
    setDisplayShowAllButton(false);
  }, [setShowAll, setDisplayShowAllButton]);

  // The fragment in this mutation must match the query above
  const [mutate] = useMutation(gql(`
    mutation addOrUpvoteTag($tagId: String, $postId: String) {
      addOrUpvoteTag(tagId: $tagId, postId: $postId) {
        ...TagRelMinimumFragment
      }
    }
  `));

  const onTagSelected = useCallback(async ({tagId, tagName}: {tagId: string, tagName: string}) => {
    try {
      setIsAwaiting(true);
      await mutate({
        variables: {
          tagId: tagId,
          postId: post._id,
        },
      });
      setIsAwaiting(false);
      void refetch();
      captureEvent("tagAddedToItem", {tagId, tagName});
    } catch (e) {
      setIsAwaiting(false);
      flash(e.message);
    }
  }, [setIsAwaiting, mutate, refetch, post._id, captureEvent, flash]);

  const contentTypeInfo = forumSelect(getContentTypes(forumType), forumType);

  const PostTypeTag = useCallback(({tooltipBody, label, neverCoreStyling, showAutoClassifiedIcon}: {
    tooltipBody: ReactNode,
    label: string,
    neverCoreStyling?: boolean,
    showAutoClassifiedIcon?: boolean
  }) => {
    return (
      <HoverOver
        title={
          <Card className={classes.card}>
            <ContentStyles contentType="comment">
              {tooltipBody}
            </ContentStyles>
          </Card>
        }
        tooltip={false}
      >
        <div className={classNames(classes.frontpageOrPersonal, {
          [classes.smallText]: smallText,
          [classes.noBackground]: noBackground,
          [classes.neverCoreStyling]: neverCoreStyling,
        })}>
          {label}
          {showAutoClassifiedIcon && <AutoClassifiedIcon />}
        </div>
      </HoverOver>
    );
  }, [classes, smallText, noBackground]);

  // Post type is either Curated, Frontpage, Personal, or uncategorized (in which case
  // we don't show any indicator). It's uncategorized if it's not frontpaged and doesn't
  // have reviewedByUserId set to anything.
  const showAutoClassifiedIcon = !post.curatedDate && !!post.reviewedByUserId && post.reviewedByUserId === adminAccountSetting.get(forumType)?._id;

  let postType = post.curatedDate
    ? <MaybeLink to={contentTypeInfo.curated.linkTarget} className={classes.postTypeLink}>
        <PostTypeTag label="Curated" tooltipBody={contentTypeInfo.curated.tooltipBody} neverCoreStyling={neverCoreStyling}/>
      </MaybeLink>
    : (post.frontpageDate
      ? <MaybeLink to={contentTypeInfo.frontpage.linkTarget} className={classes.postTypeLink}>
          <PostTypeTag label="Frontpage" tooltipBody={contentTypeInfo.frontpage.tooltipBody} neverCoreStyling={neverCoreStyling} showAutoClassifiedIcon={showAutoClassifiedIcon}/>
        </MaybeLink>
      : (post.reviewedByUserId
        ? <MaybeLink to={contentTypeInfo.personal.linkTarget} className={classes.postTypeLink}>
            <PostTypeTag label="Personal Blog" tooltipBody={contentTypeInfo.personal.tooltipBody} neverCoreStyling={neverCoreStyling} showAutoClassifiedIcon={showAutoClassifiedIcon}/>
          </MaybeLink>
        : null
      )
    )

  const eventTag = contentTypeInfo.event && post.isEvent ? <MaybeLink to={contentTypeInfo.event.linkTarget} className={classes.postTypeLink}>
    <PostTypeTag label="Event" tooltipBody={contentTypeInfo.event.tooltipBody} neverCoreStyling={neverCoreStyling}/>
  </MaybeLink> : null

  const sortedTagInfo = results
    ? stableSortTags(results.filter((tagRel) => !!tagRel?.tag).map((tr) => ({ tag: tr.tag!, tagRel: tr })), {
        coreTags: "last",
      })
    : post.tags.map((tag) => ({ tag, tagRel: undefined }));
  const menuPlacement = useAltAddTagButton ? "bottom-end" : undefined;

  const addTagButton = <AddTagButton onTagSelected={onTagSelected} isVotingContext menuPlacement={menuPlacement}>
    {useAltAddTagButton && <span className={classNames(classes.altAddTagButton, noBackground && classes.noBackground)}>+</span>}
  </AddTagButton>

  const postYear = post.postedAt ? new Date(post.postedAt).getFullYear() : null; // 2023
  const now = useCurrentTime();
  const currentYear = now.getFullYear(); // 2025
  const isRecent = postYear && ((currentYear - postYear) < 2);

  const tagIds = (results ? results.map((tagRel) => tagRel.tag?._id) : post.tags.map((tag) => tag._id)).filter(
    (tagId): tagId is string => !!tagId
  );

  const innerContent = (
    <>
      {!tagRight && currentUserId && !hideAddTag && addTagButton}
      {showCoreTags && (
        <CoreTagsChecklist existingTagIds={tagIds} onTagSelected={onTagSelected} />
      )}
      {sortedTagInfo.map(
        ({ tagRel, tag }) =>
          tag && (
            <FooterTag
              key={tag._id}
              tagRel={tagRel}
              tag={tag}
              hoverable="ifDescriptionPresent"
              hideScore={hideScore}
              smallText={smallText}
              highlightAsAutoApplied={highlightAutoApplied && tagRel?.autoApplied}
              link={link}
              noBackground={noBackground}
              neverCoreStyling={neverCoreStyling}
            />
          )
      )}
      {!hidePostTypeTag && postType}
      {eventTag}
      {annualReviewMarketInfo && isRecent && <PostsAnnualReviewMarketTag annualReviewMarketInfo={annualReviewMarketInfo} />}
      {tagRight && currentUserId && !hideAddTag && addTagButton}
      {isAwaiting && <Loading />}
    </>
  );

  return <>
    <span
      ref={rootRef}
      className={classNames(classes.root, {[classes.allowTruncate]: !showAll}, {[classes.overrideMargins] : overrideMargins, [classes.alignRight]: align === "right"})}
    >
      {innerContent}
      {appendElement}
    </span>
    {displayShowAllButton && <div className={classes.showAll} onClick={onClickShowAll}>Show all wikitags</div>}
  </>
};

const MaybeLink = ({to, children, className}: {
  to: string|null,
  children: React.ReactNode,
  className?: string,
}) => {
  if (to) {
    return <Link to={to} className={className}>{children}</Link>
  } else {
    return <>{children}</>;
  }
}

export default FooterTagList;
